import { useCallback, useRef, useState } from 'react';
import type { GenericAttachment } from '@/styles/GenericAttachmentsSection';
import type { AttachmentLike } from '@/hooks/useAttachmentStaging';

/**
 * The same "pending upload" pattern as useAttachmentStaging, for a form that
 * edits SEVERAL sub-records at once (a Selection's options, an invoice's
 * lines, ...), each with its own attachments. State is kept per `key`
 * (the sub-record's local id).
 *
 *   - Sub-record already exists server-side (`entityId` given): the files are
 *     uploaded immediately, like every edit-mode form.
 *   - Sub-record not saved yet (`entityId` null): the files are STAGED and
 *     shown immediately as "pending upload" (thumbnail + delete) -- not an
 *     error, just waiting for a parent to exist. `uploadAllPending` persists
 *     them once the sub-records have real ids; the failure count it returns
 *     lets the caller say so honestly instead of pretending it worked.
 *   - Removing a staged file just drops it; removing an uploaded one deletes
 *     it through `deleteFn`.
 *
 * (The upload itself rolls back its Storage object when the server refuses
 * the association -- see lib/entity-attachments.ts -- so a failed link never
 * leaves an orphaned file.)
 */
export interface UseKeyedAttachmentStagingOptions<T extends AttachmentLike = GenericAttachment> {
  uploadFn: (entityId: string, files: File[]) => Promise<T[]>;
  deleteFn?: (attachmentId: string) => Promise<boolean>;
  onError?: (message: string) => void;
}

interface Slot<T> {
  pending: File[];
  uploaded: T[];
}

const EMPTY: never[] = [];

export function useKeyedAttachmentStaging<T extends AttachmentLike = GenericAttachment>({
  uploadFn,
  deleteFn,
  onError,
}: UseKeyedAttachmentStagingOptions<T>) {
  const [slots, setSlots] = useState<Record<string, Slot<T>>>({});
  const slotsRef = useRef(slots);
  slotsRef.current = slots;

  const update = useCallback((key: string, fn: (slot: Slot<T>) => Slot<T>) => {
    setSlots((prev) => ({ ...prev, [key]: fn(prev[key] ?? { pending: [], uploaded: [] }) }));
  }, []);

  const pendingFor = useCallback((key: string): File[] => slots[key]?.pending ?? EMPTY, [slots]);
  const uploadedFor = useCallback((key: string): T[] => slots[key]?.uploaded ?? (EMPTY as T[]), [slots]);

  /** Replace what is already uploaded for `key` (e.g. just fetched from the server). */
  const seed = useCallback((key: string, uploaded: T[]) => {
    update(key, (slot) => ({ ...slot, uploaded }));
  }, [update]);

  const upload = useCallback(async (key: string, entityId: string | null | undefined, files: File[]) => {
    if (!files || files.length === 0) return;
    if (!entityId) {
      update(key, (slot) => ({ ...slot, pending: [...slot.pending, ...files] }));
      return;
    }
    try {
      const uploaded = await uploadFn(entityId, files);
      if (uploaded.length < files.length) onError?.('Some files could not be uploaded');
      if (uploaded.length > 0) update(key, (slot) => ({ ...slot, uploaded: [...slot.uploaded, ...uploaded] }));
    } catch (error) {
      console.error('[useKeyedAttachmentStaging] upload failed', error);
      onError?.('Failed to upload files');
    }
  }, [uploadFn, update, onError]);

  const removePending = useCallback((key: string, index: number) => {
    update(key, (slot) => ({ ...slot, pending: slot.pending.filter((_, i) => i !== index) }));
  }, [update]);

  const remove = useCallback(async (key: string, attachmentId: string): Promise<boolean> => {
    if (!deleteFn) return false;
    try {
      const ok = await deleteFn(attachmentId);
      if (ok) update(key, (slot) => ({ ...slot, uploaded: slot.uploaded.filter((a) => a.id !== attachmentId) }));
      else onError?.('Failed to delete file');
      return ok;
    } catch (error) {
      console.error('[useKeyedAttachmentStaging] delete failed', error);
      onError?.('Failed to delete file');
      return false;
    }
  }, [deleteFn, update, onError]);

  /** Forget a sub-record's state (it was removed from the form). Staged files are simply dropped. */
  const discard = useCallback((key: string) => {
    setSlots((prev) => {
      if (!(key in prev)) return prev;
      const { [key]: _removed, ...rest } = prev;
      return rest;
    });
  }, []);

  const reset = useCallback(() => setSlots({}), []);

  /**
   * Persist every staged file once its sub-record has a real id.
   * @param entityIdByKey local key -> real id (keys without one are left staged)
   * @returns how many files went up and how many could not
   */
  const uploadAllPending = useCallback(async (entityIdByKey: Record<string, string>): Promise<{ uploaded: number; failed: number }> => {
    let uploadedCount = 0;
    let failed = 0;
    for (const [key, slot] of Object.entries(slotsRef.current)) {
      const entityId = entityIdByKey[key];
      if (!entityId || slot.pending.length === 0) continue;
      try {
        const uploaded = await uploadFn(entityId, slot.pending);
        uploadedCount += uploaded.length;
        failed += slot.pending.length - uploaded.length;
        update(key, (current) => ({ pending: [], uploaded: [...current.uploaded, ...uploaded] }));
      } catch (error) {
        console.error('[useKeyedAttachmentStaging] upload-after-create failed', error);
        failed += slot.pending.length;
      }
    }
    return { uploaded: uploadedCount, failed };
  }, [uploadFn, update]);

  return { pendingFor, uploadedFor, seed, upload, removePending, remove, discard, reset, uploadAllPending };
}

/** The hook's return value, for components that receive it as a prop. */
export type KeyedAttachmentStaging<T extends AttachmentLike = GenericAttachment> = ReturnType<typeof useKeyedAttachmentStaging<T>>;
